<template>
  <div>
    <div class="content-header">
      <div class="container-fluid">
        <div class="row mb-2">
          <div class="col-sm-6">
            <h1 class="m-0">{{ this.$route.name }}</h1>
          </div>
          <div class="col-sm-6">
            <ol class="breadcrumb float-sm-right">
              <li class="breadcrumb-item active">
                <i v-bind:class="routeIcon"></i> {{ $t(this.$route.name) }}
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>

    <EditOperatorForm ref="editModal" v-on:finish="handleItemsUpdate" @hidden="closeEditOperatorModal" @create-template="openTemplate" />
    <CreateOperatorFromTemplate v-if="isAdmin" ref="templateModal" @finish="handleItemsUpdate" />
    <OperatorTemplateOperations v-if="isAdmin" ref="templateOperations" @finish="handleItemsUpdate" />
    <EditJsonOperatorForm ref="editJsonModal" v-on:finish="handleItemsUpdate" @hidden="closeEditOperatorModal" />

    <section class="content">
      <div class="container-fluid">
        <div class="row col-lg-12">
          <div class="form-group col-sm-12">
            <b-button type="button" variant="success" @click="openEditOperator({}, true)">
              <i class="fas fa-plus"></i> Create
            </b-button>
            <b-button type="button" class="ml-1" variant="info" :disabled="isSyncing" @click="syncAllOperators">
              <i :class="'fas ' + (isSyncing ? 'fa-spinner fa-pulse' : 'fa-broadcast-tower')
                "></i>
              Sync all
            </b-button>
          </div>
        </div>

        <b-row class="col-lg-12">
          <div class="col-lg-3 col-md-3 col-sm-12" v-for="{ id, label } in filterInputs" v-bind:key="id">
            <b-form-group label-cols-lg="3" :label-for="id + '-filter-input'" :label="label">
              <b-form-input :id="id + '-filter-input'" v-model="filters[id]" autocomplete="off"
                :placeholder="'Enter ' + label" />
            </b-form-group>
          </div>
          <div class="col-lg-3 col-md-3 col-sm-12">
            <b-form-group label-cols-lg="3" label-for="status-filter-input" label="Status">
              <b-form-select
                id="status-filter-input"
                v-model="filters.status"
                :options="statusFilterOptions"
              />
            </b-form-group>
          </div>
        </b-row>

        <b-row class="col-lg-12">
          <div class="col-lg-12 col-md-12 col-sm-12 apply-clear">
            <FilterButtons :cancel="this.cancel" :loadFn="loadItems" :cancelFn="cancelRequest"
              :resetFn="clearFilters" />
          </div>
        </b-row>

        <div class="row col-12" id="loading-container">
          <div class="col-12 mb-10">
            <div class="mb-10">
              <div class="table-wrapper mb-10">
                <b-table :striped="true" :small="true" :hover="true" :empty-text="emptyText" show-empty :busy="!!cancel"
                  :items="items" :fields="fields" :sort-by="sortBy" :sort-desc="sortDesc" :no-local-sorting="true"
                  :sort-icon-left="true" @sort-changed="sortingChanged">
                  <template #cell(logo)="row">
                    <div v-if="row.item.logo">
                      <b-img style="max-height: 20px" :src="makeLogoPath(row.item.logo)" fluid></b-img>
                    </div>
                  </template>
                  <template #cell(status)="row">
                    <b-badge v-if="row.item.provisioningState === 'incomplete'" variant="warning">Provisioning incomplete</b-badge>
                    <b-badge v-else :variant="statusVariant(row.item.status)">
                      {{ statusLabel(row.item.status) }}
                    </b-badge>
                  </template>
                  <template #cell(actions)="row">
                    <b-button v-if="isAdmin && row.item.status !== REMOVED && row.item.provisioningState !== 'incomplete'"
                      :variant="isActive(row.item.status) ? 'warning' : 'success'" size="xs" class="mr-1"
                      :disabled="activityPending[row.item.id]" @click="setActivity(row.item)"
                      :title="isActive(row.item.status) ? 'Deactivate operator' : 'Activate operator'">
                      <i :class="isActive(row.item.status) ? 'fas fa-pause' : 'fas fa-play'"></i>
                      {{ isActive(row.item.status) ? 'Deactivate' : 'Activate' }}
                    </b-button>
                    <template v-if="isActive(row.item.status) && row.item.provisioningState !== 'incomplete'">
                      <b-button v-if="isAdmin" variant="outline-primary" class="mr-1" size="xs"
                        v-b-tooltip.hover title="Create operator from this template" aria-label="Create operator from this template"
                        @click="openTemplate(row.item.id)">
                        <i class="fas fa-copy" aria-hidden="true"></i>
                      </b-button>
                      <b-button variant="primary" class="mr-1" size="xs" @click="openEditOperator(row.item, false)">
                        <i class="fas fa-edit"></i>&nbsp;Edit
                      </b-button>
                      <b-button variant="primary" class="mr-1" size="xs" @click="openEditJsonOperator(row.item)">
                        <i class="fas fa-edit"></i>&nbsp; JSON
                      </b-button>
                      <RemoveForm
                        v-if="isAdmin && row.item.canDelete !== false"
                        :object="row.item.name"
                        message="Please confirm removing operator"
                        :url="'/api/v1/operators/' + row.item.id"
                        @finish="handleItemsUpdate"
                      />
                      <b-button
                        v-else-if="isAdmin"
                        variant="secondary"
                        class="mr-1"
                        size="xs"
                        disabled
                        v-b-tooltip.hover
                        title="Cannot delete: operator has financial activity"
                      >
                        <i class="fas fa-trash"></i>&nbsp;Remove
                      </b-button>
                    </template>
                    <RestoreForm
                      v-if="row.item.status === REMOVED && isAdmin && row.item.provisioningState !== 'incomplete'"
                      :object="row.item.name"
                      message="Please confirm restoring operator"
                      :url="'/api/v1/operators/' + row.item.id + '/restore'"
                      @finish="handleItemsUpdate"
                    />
                  </template>
                </b-table>
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <b-pagination class="float-right" v-model="pagination.page" :total-rows="pagination.records"
                  :per-page="pagination.onPage" @change="onPageChange" first-number last-number />
              </div>
            </div>

            <div class="row mb-10">
              <div class="col-12">
                <ve-pagination class="float-right" :page-index="pagination.page" :page-size="pagination.onPage"
                  :total="pagination.records" :pagingCount="pagination.count" :layout="['total', 'sizer']"
                  :pageSizeOption="pagination.pageSizeOption" @on-page-size-change="onPageSizeChange" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import axios from "axios";
import Multiselect from "vue-multiselect";
import FilterButtons from "../../components/FilterButtons.vue";
import EditOperatorForm from "../../components/Operators/EditOperatorForm.vue";
import EditJsonOperatorForm from "../../components/Operators/EditJsonOperatorForm.vue";
import CreateOperatorFromTemplate from "../../components/Operators/CreateOperatorFromTemplate.vue";
import OperatorTemplateOperations from "../../components/Operators/OperatorTemplateOperations.vue";
import RemoveForm from "../../components/remove-form/RemoveForm.vue";
import RestoreForm from "../../components/remove-form/RestoreForm.vue";
import { EXISTS, REMOVED, INACTIVE } from "../../definitions/entityStatus";
import * as Roles from "../../definitions/Roles";
import {
  isOperatorActive,
  operatorStatusLabel,
  operatorStatusVariant,
} from "../../utils/operatorStatus";
import { getRouteIcon, ROUTE_OPERATORS } from "../../utils/routerIcons";

function getDefaultPagination() {
  return {
    onPage: 10,
    page: 1,
    records: 0,
    count: 5,
    pageSizeOption: [10, 25, 50, 100, 200],
  };
}

function getDefaultFilters() {
  return {
    name: null,
    secret: null,
    partnerName: null,
    generalOperatorId: null,
    status: EXISTS,
  };
}

export default {
  components: {
    RemoveForm,
    RestoreForm,
    Multiselect,
    FilterButtons,
    EditOperatorForm,
    EditJsonOperatorForm,
    CreateOperatorFromTemplate,
    OperatorTemplateOperations,
  },
  data() {
    return {
      isSyncing: false,
      REMOVED,
      activityPending: {},
      cancel: null,
      error: null,
      filterInputs: [
        { label: "Name", id: "name" },
        { label: "Secret", id: "secret" },
        { label: "Gen. Op. Id", id: "generalOperatorId" },
        { label: "Group", id: "partnerName" },
      ],
      filters: getDefaultFilters(),
      items: [],
      sortBy: null,
      sortDesc: null,
      pagination: getDefaultPagination(),
      editOperator: null,
      isCreate: null,
    };
  },
  computed: {
    backUrl() {
      return process.env.VUE_APP_BACK_URL;
    },
    cdnBackUrl() {
      return process.env.VUE_APP_CDN_BACK_URL;
    },
    admApiUrl() {
      return process.env.VUE_APP_ADM_API_URL;
    },
    statusFilterOptions() {
      return [
        { value: null, text: "All" },
        { value: EXISTS, text: "Active" },
        { value: INACTIVE, text: "Inactive" },
        { value: REMOVED, text: "Deleted" },
      ];
    },
    fields() {
      return [
        { key: "logo", label: "Logo", sortable: false },
        { key: "name", label: "Name", sortable: true },
        { key: "generalOperatorId", label: "General Operator Id", sortable: true },
        { key: "secret", label: "Secret", sortable: true },
        { key: "partnerName", label: "Group", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "actions", label: "Actions" },
      ];
    },

    emptyText() {
      if (this.cancel) {
        return "Loading items..";
      }

      if (this.error) {
        return this.error;
      }

      return "There are no records to show";
    },
    routeIcon: () => getRouteIcon(ROUTE_OPERATORS),
    isAdmin() {
      const user = this.$store.getters.currentUser;

      return user && user.type === Roles.ADMIN;
    },
  },
  methods: {
    async setActivity(operator) {
      if (this.activityPending[operator.id]) return;
      this.$set(this.activityPending, operator.id, true);
      try {
        await this.$http.post('/api/v1/operators/' + operator.id + '/activity', {active: !this.isActive(operator.status)});
        await this.loadItems();
        await this.$store.dispatch('operatorsForSelect/reloadList');
        await this.$store.dispatch('limits/reloadOperatorsForSelect');
        this.$toastr.i('Success');
      } catch (error) {
        this.$toastr.e(error.response?.data?.message || 'Unable to change operator activity');
      } finally {
        this.$delete(this.activityPending, operator.id);
      }
    },
    openTemplate(sourceId) {
      if (this.isAdmin) this.$refs.templateModal.show(sourceId);
    },
    isActive(status) {
      return isOperatorActive(status);
    },
    statusLabel(status) {
      return operatorStatusLabel(status);
    },
    statusVariant(status) {
      return operatorStatusVariant(status);
    },
    makeLogoPath(path) {
      if (path[0] === "/") {
        return this.cdnBackUrl + path;
      }

      return this.cdnBackUrl + "/" + path;
    },
    closeEditOperatorModal() {
      this.editOperator = null;
      this.isCreate = null;
    },
    openEditOperator(operator, isCreate) {
      this.editOperator = operator;
      this.isCreate = isCreate;
      this.$refs.editModal.show(
        { ...operator },
        { ...(operator.settings || {}) },
        isCreate
      );
    },
    openEditJsonOperator(operator) {
      this.$refs.editJsonModal.show(operator);
    },
    clearFilters() {
      this.filters = getDefaultFilters();
      this.sortingChanged({
        sortBy: null,
        sortDesc: null,
      });
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.pagination.page = 1;

      this.loadItems();
    },
    handleItemsUpdate() {
      if (this.$refs.templateOperations) this.$refs.templateOperations.load();
      Promise.all([
        this.$store.dispatch('operatorsForSelect/reloadList'),
        this.$store.dispatch('limits/reloadOperatorsForSelect'),
      ]).then(() => {
        this.loadItems();
      });
    },
    syncAllOperators() {
      if (this.isSyncing) {
        return;
      }

      this.isSyncing = true;

      this.$http({
        url: "/api/v1/operators/sync-all",
        method: "POST",
      })
        .then(() => {
          this.$toastr.i("Success");
        })
        .catch((err) => {
          console.error(err);
          this.$toastr.e("Failed");
        })
        .finally(() => {
          this.isSyncing = false;
        });
    },
    loadItems() {
      let loadingInstance = this.$veLoading({
        target: document.querySelector("#loading-container"),
        name: "wave",
      });

      this.items = [];

      let self = this;
      loadingInstance.show();

      this.$http({
        url: "/api/v1/operators",
        params: {
          ...this.filters,
          // pagination
          limit: this.pagination.onPage,
          offset: this.pagination.onPage * (this.pagination.page - 1),

          // sort params
          orderBy: this.sortBy,
          direction: this.sortDesc ? "DESC" : "ASC",
        },
        method: "GET",
        cancelToken: new axios.CancelToken((c) => {
          self.cancel = c;
        }),
      })
        .then((resp) => {
          let {
            items,
            pagination: { limit, totalCount },
          } = resp.data;

          this.items = items;
          this.pagination.onPage = limit;
          this.pagination.records = totalCount;
        })
        .catch((err) => {
          this.items = [];
          this.error = err;
        })
        .finally(() => {
          loadingInstance.close();
          this.cancel = null;
        });
    },
    cancelRequest() {
      if (this.cancel) {
        this.cancel("this -> cancel previous request");
      }
    },
    onPageChange(page) {
      this.pagination.page = page;
      this.loadItems();
    },
    onPageSizeChange(pageSize) {
      this.pagination.page = 1;
      this.pagination.onPage = pageSize;
      this.loadItems();
    },
  },
  async beforeMount() {
    await this.$store.dispatch("partnersForSelect/loadList");
    await this.$store.dispatch("tablesForSelect/loadList");
  },
  mounted() {
    this.loadItems();
  },
};
</script>
